/**
 * Every tab, checked line by line - on the Overview, beside where the figures came from.
 *
 * The server (v2_tab_audit) states every equality the tabs owe each other - a Control
 * Account section rolls and closes on the Balance Sheet, the Fixed Assets tab is the
 * Balance Sheet's and the P&L's, the Cashflow closes on the Balance Sheet's cash - and
 * every figure the filing prints that a tab should reproduce, and says which hold. It is
 * recomputed from the saved model and the filing's own text, so an edit is re-checked at
 * once. A check that fails names both figures and where the second came from, so nobody
 * has to take "verified" on trust.
 */

import { useEffect, useState } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { TabAudit, TabAuditCheck } from '../../../types/financial-model-v2';
import { formatMoney } from '../../../lib/currency';

/** The order a reader meets the tabs in. */
export const TAB_ORDER = [
  'P&L',
  'Balance Sheet',
  'Cashflow',
  'Control Account',
  'Fixed Assets',
  'COGS',
  'Equity',
];

/** The tabs in reading order, with the totals across them. */
export function auditSummary(audit: TabAudit | null) {
  if (!audit) return null;
  const rank = (tab: string) => {
    const index = TAB_ORDER.indexOf(tab);
    return index === -1 ? TAB_ORDER.length : index;
  };
  const tabs = [...audit.tabs].sort((a, b) => rank(a.tab) - rank(b.tab));
  const checks = tabs.reduce((sum, tab) => sum + tab.checks, 0);
  const hold = tabs.reduce((sum, tab) => sum + tab.hold, 0);
  return { tabs, checks, hold, failing: checks - hold };
}

function Failing({ check, currency }: { check: TabAuditCheck; currency: string }) {
  const gap = check.model - check.against;
  return (
    <li className="grid grid-cols-[3.5rem_1fr] gap-x-2 py-1 text-xs text-gray-700">
      <span className="font-medium text-gray-900">FY{check.year}</span>
      <span>
        <span className="font-medium text-gray-900">{check.line}</span>
        <span className="block">
          Model {formatMoney(check.model, currency)} against{' '}
          {formatMoney(check.against, currency)} ({check.source}) - out by{' '}
          <span className="font-medium text-amber-800">{formatMoney(gap, currency)}</span>
        </span>
        {check.note && <span className="block text-gray-500">{check.note}</span>}
      </span>
    </li>
  );
}

export function ChecksPanel() {
  const model = useFinancialModelV2Store((s) => s.model);
  const audit = useFinancialModelV2Store((s) => s.tabAudit);
  const loadTabAudit = useFinancialModelV2Store((s) => s.loadTabAudit);
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState<string | null>(null);

  useEffect(() => {
    void loadTabAudit();
  }, [loadTabAudit, model?.version]);

  const summary = auditSummary(audit);
  if (!model || !audit || !summary || summary.checks === 0) return null;
  const currency = model.settings.baseCurrency;

  return (
    <section
      className="mt-4 rounded-xl border border-gray-200 bg-white px-4 py-3"
      aria-label="Every tab checked line by line"
      data-testid="checks-panel"
    >
      <button
        type="button"
        className="flex w-full items-center justify-between text-left"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-gray-900">
          Every tab checked line by line
        </span>
        <span className="text-xs text-gray-600">
          {summary.failing
            ? `${summary.hold.toLocaleString()} of ${summary.checks.toLocaleString()} checks hold`
            : `All ${summary.checks.toLocaleString()} checks hold`}
          <span className="ml-2">{open ? 'Hide' : 'Show'}</span>
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-gray-600">
            {audit.hasDocument
              ? 'Each tab against the others, and against the figures the filing prints.'
              : 'Each tab against the others - this model has no filing to check against.'}
          </p>
          {summary.tabs.map((tab) => {
            const clean = tab.failing.length === 0;
            return (
              <div
                key={tab.tab}
                className={`rounded-lg border px-3 py-2 ${
                  clean ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
                }`}
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-left text-xs"
                  disabled={clean}
                  onClick={() => setShown((value) => (value === tab.tab ? null : tab.tab))}
                  aria-expanded={shown === tab.tab}
                >
                  <span className="font-semibold text-gray-900">{tab.tab}</span>
                  <span className="text-gray-700">
                    {tab.hold} of {tab.checks} hold
                    {!clean && <span className="ml-2">{shown === tab.tab ? 'Hide' : 'Show'}</span>}
                  </span>
                </button>
                {!clean && shown === tab.tab && (
                  <ul className="mt-1 divide-y divide-amber-100">
                    {tab.failing.map((check) => (
                      <Failing
                        key={`${check.line}-${check.year}-${check.source}`}
                        check={check}
                        currency={currency}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
